// src/App.js (완전한 최종본)

import React, { useState } from 'react';
import WalletConnect from './components/WalletConnect';
import TradeForm from './components/TradeForm';
import TradeList from './components/TradeList';
import AdminPanel from './components/AdminPanel';
import { useWallet } from './hooks/useWallet';
import { getCurrentFeePercent, getCurrentFeePercentage, feePercentToPercentage, DEFAULT_FEE_PERCENT } from './utils/contract';

import './App.css';

function App() {
  const { account, isCorrectNetwork, networkInfo, balance } = useWallet();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [currentFeePercentage, setCurrentFeePercentage] = useState(feePercentToPercentage(DEFAULT_FEE_PERCENT));

  // 연결 상태 표시
  const getConnectionStatus = () => {
    if (!account) {
      return (
        <div className="status-indicator disconnected">
          <span className="status-dot"></span>
          지갑 연결 필요
        </div>
      );
    }
    
    if (!isCorrectNetwork) {
      return (
        <div className="status-indicator wrong-network">
          <span className="status-dot"></span>
          네트워크 전환 필요
        </div>
      );
    }
    
    return (
      <div className="status-indicator connected">
        <span className="status-dot"></span>
        {networkInfo?.isTestnet ? '테스트넷' : '메인넷'} 연결됨
      </div>
    );
  };

  // 계정 정보 표시
  const getAccountInfo = () => {
    if (!account) return null;

    return (
      <div className="account-info-header">
        <div className="account-address">
          <span className="address-label">연결된 지갑:</span>
          <span className="address-value">{account.slice(0, 6)}...{account.slice(-4)}</span>
          <button 
            className="copy-button"
            onClick={() => {
              navigator.clipboard.writeText(account);
              alert('지갑 주소가 복사되었습니다!');
            }}
            title="주소 복사"
          >
            📋
          </button>
        </div>
        <div className="account-balance">
          <span className="balance-label">잔액:</span>
          <span className="balance-value">{balance} WLC</span>
        </div>
      </div>
    );
  };

  // 탭 렌더링
  const renderContent = () => {
    if (!account || !isCorrectNetwork) {
      return <WalletConnect />;
    }

    switch (activeTab) {
      case 'create':
        return <TradeForm />;
      case 'list':
        return <TradeList />;
      case 'admin':
        return <AdminPanel />;
      case 'dashboard':
      default:
        return (
          <div className="app-content">
            <h2>거래 대시보드</h2>
            <div className="guide-notice">
              <div className="guide-header">
                <span className="guide-icon">⚠️</span>
                <h3> 스마트 P2P 거래 방법</h3>
              </div>
              <div className="guide-content">
                <p><strong>1. 거래 개시:</strong> 판매자는 거래 생성 및 코인 보내기 버튼을 눌러서 거래를 개신합니다.</p>
                <p><strong>2. 원화 송금:</strong> 구매자는 거래 목록을 조회하여 코인 락업을 확인하고, 원화를 송금한 후 송금확인증을 제출합니다.</p>
                <p><strong>3. 입금 확인:</strong> 판매자는 거래 목록에 입금확인 요청이 발생하면 계좌에 원화가 입금된 경우 입금사실을 확인해줍니다.</p>
                <p><strong>4. 코인 수신:</strong> 구매자는 메타마스크 지갑에 약속한 코인이 수신되었는지 확인합니다.</p> 
              </div>
            </div>
            
            <div className="feature-grid">
              <div className="feature-card">
                <span className="feature-icon">🔄</span>
                <h3>거래 생성</h3>
                <p>WLC를 원화로 판매하거나 구매할 수 있는 거래를 생성합니다.</p>
                <button 
                  className="feature-button"
                  onClick={() => setActiveTab('create')}
                >
                  1. 거래 생성 및 코인 보내기
                </button>
              </div>
              
              <div className="feature-card">
                <span className="feature-icon">📋</span>
                <h3>거래 목록</h3>
                <p>진행 중인 거래를 처리하고 완료된 거래 내역을 확인할 수 있습니다.</p>
                <button 
                  className="feature-button"
                  onClick={() => setActiveTab('list')}
                >
                  2. 거래 목록 조회 및 처리
                </button>
              </div>
              
              <div className="feature-card">
                <span className="feature-icon">🔒</span>
                <h3>안전한 거래</h3>
                <p>스마트 컨트랙트를 통한 자동화된 안전한 거래 시스템입니다.</p>
                <button 
                  className="feature-button"
                  onClick={() => window.open('https://scan.worldland.foundation/address/0xc44d3ec358353c3f4639bdfd0f40b936f2c56d0a', '_blank')}
                >
                  컨트랙트 보기
                </button>
              </div>
            </div>

            <div className="disclaimer-notice">
              <div className="disclaimer-header">
                <span className="disclaimer-icon">⚠️</span>
                <h3>거래 안내 및 면책사항</h3>
              </div>
              <div className="disclaimer-content">
                <p><strong>• 소액 거래 권장:</strong> 안전한 거래를 위해 5,000 WLC 이하의 소액 거래를 권장합니다.</p>
                <p><strong>• 개인 책임:</strong> 모든 거래의 책임은 당사자 본인에게 있으며, 플랫폼은 중개 역할만을 수행합니다.</p>
                <p><strong>• 거래 검증:</strong> 거래 전 상대방의 신원과 거래 조건을 반드시 확인하시기 바랍니다.</p>
                <p><strong>• 스마트 컨트랙트:</strong> 블록체인 기술로 자동화된 안전장치가 적용되어 있습니다.</p> 
              </div>
            </div>
            
            <div className="contract-info">
              <h3>컨트랙트 정보</h3>
              <p><strong>주소:</strong> 0xc44d3ec358353c3f4639bdfd0f40b936f2c56d0a</p>
              <p><strong>네트워크:</strong> {networkInfo?.chainName}</p>
              <p><strong>상태:</strong> 배포 완료 및 활성화</p>
              <p><strong>수수료:</strong> 거래 완료 시 <strong>{currentFeePercentage}</strong> (누적 후 관리자가 출금)</p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="App">
      <header className="app-header">
        <div style={{zIndex: '1'}}>
          <h1>월드랜드 P2P 거래 서비스</h1>
          <p>WLC와 원화 간 안전한 개인 거래</p>
        </div>

        <div style={{marginTop: '-80px', zIndex: '2', display: 'flex', justifyContent: 'right', flexDirection: 'row'}}>
          {getConnectionStatus()} 
        </div>

        <div style={{marginTop: '-80px', zIndex: '2', display: 'flex', justifyContent: 'left', flexDirection: 'row'}}>
          {getAccountInfo()}
        </div>
        
        {/* 네비게이션 탭 */}
        {account && isCorrectNetwork && (
          <nav className="nav-tabs">
            <button 
              className={`nav-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
              onClick={() => setActiveTab('dashboard')}
            >
              대시보드
            </button>
            <button 
              className={`nav-tab ${activeTab === 'create' ? 'active' : ''}`}
              onClick={() => setActiveTab('create')}
            >
              1. 거래 생성 및 코인 보내기
            </button>
            <button 
              className={`nav-tab ${activeTab === 'list' ? 'active' : ''}`}
              onClick={() => setActiveTab('list')}
            >
              2. 거래 목록 조회 및 처리
            </button>
            <button 
              className={`nav-tab ${activeTab === 'admin' ? 'active' : ''}`}
              onClick={() => setActiveTab('admin')}
            >
              관리자
            </button>
          </nav>
        )}
      </header>

      <main className="app-main">
        {renderContent()}
      </main>

      <footer className="app-footer">
        <p>월드랜드 블록체인 기반 탈중앙화 P2P 거래소</p>
        <p>스마트 컨트랙트로 보장되는 안전한 거래</p>
        <p>컨트랙트: 0xc44d3ec358353c3f4639bdfd0f40b936f2c56d0a</p>
      </footer>
    </div>
  );
}

export default App;